import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ComposedChart,
} from 'recharts';
import { KPICell, SectionFrame, performanceColor, type PeriodContentProps } from './shared';
import { CircularProgress } from '../CircularProgress';
import { useQualityMeasureLabel } from '../../../lib/qualityLabels';
import { PerWeekDrilldownModal, type PerWeekDrilldownMode } from './PerWeekDrilldownModal';
import { YTDDrilldownModal } from './YTDDrilldownModal';
import type { WeekTrendPoint } from '../../../lib/api/scheduled-dashboard';

const PALETTE = {
  otif: '#0f766e',
  otd:  '#0284c7',
  spend: '#2563eb',
  arrears: '#dc2626',
  ncr: '#dc2626',
  ppk: '#0284c7',
  target: '#94a3b8',
};

const TARGET_PCT = 95;

/** Default ERP order type for the Scheduled-dashboard tab — '4'
 *  (scheduling agreement). Used as a fallback when the dashboard shell
 *  hasn't propagated `filters` yet (PeriodContentProps marks it
 *  optional). */
const DEFAULT_ERP_ORDER_TYPE: '4' = '4';

/** Narrow a recharts chart-level click event (the public types
 *  under-describe the runtime shape — see ``CurrentWeek.tsx`` for the same
 *  pattern) to extract the X-axis label of the tick under the cursor and
 *  the dataKey of the segment that was clicked. We accept `unknown` and
 *  narrow defensively so a recharts version bump doesn't break the build. */
function extractChartClick(raw: unknown): { weekLabel?: string; segment?: string } {
  if (!raw || typeof raw !== 'object') return {};
  const obj = raw as { activeLabel?: unknown; activePayload?: unknown };
  const ap = obj.activePayload;
  const first = Array.isArray(ap) ? ap[0] : null;
  let segment: string | undefined;
  if (first && typeof first === 'object') {
    const dk = (first as { dataKey?: unknown }).dataKey;
    if (typeof dk === 'string') segment = dk;
  }
  const lbl = obj.activeLabel;
  return {
    weekLabel: typeof lbl === 'string' ? lbl : undefined,
    segment,
  };
}

export function PerWeek({ data, formatCurrency, filters }: PeriodContentProps) {
  const qualityLabel = useQualityMeasureLabel();

  // Mirror the gating used by CurrentWeek / DefaultTab / SiteKPITab so the
  // customer's PerformanceSettings.delivery_metric_mode controls whether
  // OTIF, OTD, or both blocks appear here too.
  const deliveryMode = data.delivery_metric_mode ?? 'both';
  const showOTIF = deliveryMode === 'both' || deliveryMode === 'otif_only';
  const showOTD  = deliveryMode === 'both' || deliveryMode === 'otd_only';

  const weeks = data.per_week.weeks;
  const trend = weeks.map((w) => {
    // Weeks with no measurable schedules (total_due === 0) get a null
    // OTIF/OTD instead of 0% so the trend line breaks at empty weeks
    // — same null-guard pattern used by YTD.tsx. Without this the
    // chart shows 9 weeks of "0% OTIF" simply because no schedules
    // existed in that window, which a buyer reads as "supplier base
    // collapsed and recovered" (the opposite of the truth).
    const hasDelivery = (w.delivery.total_due ?? 0) > 0;
    return {
      week: w.label,
      otif: hasDelivery ? w.delivery.otif_pct : null,
      otd:  hasDelivery ? w.delivery.otd_pct  : null,
      received: w.cost.received_value,
      open: w.cost.open_value,
      ncrs: w.quality.ncrs,
      ppm: w.quality.ppm,
      target: TARGET_PCT,
    };
  });

  const totalReceived  = weeks.reduce((a, w) => a + w.cost.received_value, 0);
  const totalCommitted = weeks.reduce((a, w) => a + w.cost.total_value, 0);
  const totalOpen      = weeks.reduce((a, w) => a + w.cost.open_value, 0);
  const totalAppeals   = weeks.reduce((a, w) => a + (w.delivery.appeals ?? 0), 0);
  const totalNcrs = trend.reduce((a, x) => a + x.ncrs, 0);
  const last = trend[trend.length - 1];
  const lastWeek = weeks[weeks.length - 1];
  const lastOtif = last?.otif ?? 0;
  const lastOtd = last?.otd ?? 0;

  // Normalise the optional filter scope into the non-optional shape the
  // modal expects. Falls back to default ERP order type ('4', scheduling
  // agreements) since this tab is the SA dashboard.
  const modalFilters = {
    customer_id: filters?.customer_id,
    supplier_id: filters?.supplier_id,
    part_number: filters?.part_number,
    erp_order_type: filters?.erp_order_type ?? DEFAULT_ERP_ORDER_TYPE,
  };

  // 12-week window bounds — feed every "12wk" tile and aggregate chart click.
  const windowStart = weeks[0]?.week_start ?? data.today;
  const windowEnd   = weeks[weeks.length - 1]?.week_end ?? data.today;

  const [modal, setModal] = useState<{
    mode: PerWeekDrilldownMode;
    windowStart: string;
    windowEnd: string;
    contextLabel?: string;
  } | null>(null);

  // Appeals drilldown reuses the YTD modal (which calls
  // /delivery-appeals/) — schedules-as-appeal-proxy gives wildly wrong
  // counts (tile = 0 actual appeals, schedule proxy returns 4k+ rows).
  const [appealsOpen, setAppealsOpen] = useState(false);

  const openWeek = useCallback((mode: PerWeekDrilldownMode, week: WeekTrendPoint) => {
    setModal({
      mode,
      windowStart: week.week_start,
      windowEnd: week.week_end,
      contextLabel: `Week ${week.label}`,
    });
  }, []);

  const openAggregate = useCallback((mode: PerWeekDrilldownMode, label: string) => {
    setModal({
      mode,
      windowStart,
      windowEnd,
      contextLabel: label,
    });
  }, [windowStart, windowEnd]);

  const findWeek = useCallback((label: string | undefined) => {
    if (!label) return null;
    return weeks.find((w) => w.label === label) ?? null;
  }, [weeks]);

  // Per-segment click handler for the stacked schedule-value bar chart.
  // Recharts ``BarChart.onClick`` returns ``activePayload[0]`` (always the
  // first series), so a chart-level handler can't tell which stacked
  // segment the user actually clicked. Wire ``<Bar onClick>`` on each Bar
  // instead — the payload row carries ``week`` (label) so we can look up
  // the matching WeekTrendPoint and route to the right drilldown mode.
  const onCostSegmentClick = (segment: 'received' | 'open') => (raw: unknown) => {
    const labelRaw = raw && typeof raw === 'object'
      ? (raw as { payload?: { week?: unknown }; week?: unknown }).week
        ?? (raw as { payload?: { week?: unknown } }).payload?.week
      : undefined;
    if (typeof labelRaw !== 'string') return;
    const w = findWeek(labelRaw);
    if (!w) return;
    const mode: PerWeekDrilldownMode = segment === 'open' ? 'schedules-open' : 'schedules-received';
    openWeek(mode, w);
  };

  // Per-bar click handler for the NCRs bar chart (single series, but we
  // mirror the pattern for consistency and to avoid the chart-level
  // dataKey bug if a second bar is added later).
  const onNcrBarClick = (raw: unknown) => {
    const labelRaw = raw && typeof raw === 'object'
      ? (raw as { payload?: { week?: unknown }; week?: unknown }).week
        ?? (raw as { payload?: { week?: unknown } }).payload?.week
      : undefined;
    if (typeof labelRaw !== 'string') return;
    const w = findWeek(labelRaw);
    if (w) openWeek('ncrs', w);
  };

  const tileBase = 'cursor-pointer transition rounded-md hover:bg-slate-100/60 px-1 py-0.5';
  const onTileKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') (e.currentTarget as HTMLElement).click();
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <SectionFrame title="Quality" accent="red" subtitle="last 12 weeks">
          <div className="grid grid-cols-2 gap-3">
            <div
              className={tileBase}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (!lastWeek) return;
                setModal({
                  mode: 'ncrs',
                  windowStart: lastWeek.week_start,
                  windowEnd: lastWeek.week_end,
                  contextLabel: `Latest week (${lastWeek.label})`,
                });
              }}
              onKeyDown={onTileKey}
              title="View NCRs raised in the latest week"
            >
              <KPICell label={`${qualityLabel} (latest)`} value={<span className="text-2xl font-semibold tabular-nums">{(last?.ppm ?? 0).toLocaleString()}</span>} />
            </div>
            <div
              className={tileBase}
              role="button"
              tabIndex={0}
              onClick={() => openAggregate('ncrs', 'Last 12 weeks')}
              onKeyDown={onTileKey}
              title="View all NCRs raised in the last 12 weeks"
            >
              <KPICell label="NCRs (12wk)" value={<span className="text-2xl font-semibold tabular-nums">{totalNcrs.toLocaleString()}</span>} />
            </div>
          </div>
        </SectionFrame>
        <SectionFrame title="Delivery" accent="green" subtitle="last 12 weeks">
          <div
            className={`grid gap-3 ${
              showOTIF && showOTD ? 'grid-cols-3' : 'grid-cols-2'
            }`}
          >
            {showOTIF && (
              <div
                className={tileBase}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (!lastWeek) return;
                  setModal({
                    mode: 'schedules-otif',
                    windowStart: lastWeek.week_start,
                    windowEnd: lastWeek.week_end,
                    contextLabel: `Latest week (${lastWeek.label})`,
                  });
                }}
                onKeyDown={onTileKey}
                title="View OTIF-eligible schedules for the latest week"
              >
                <KPICell
                  label="OTIF (latest)"
                  value={<CircularProgress percentage={lastOtif} size={68} strokeWidth={6} color={performanceColor(lastOtif)} />}
                />
              </div>
            )}
            {showOTD && (
              <div
                className={tileBase}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (!lastWeek) return;
                  setModal({
                    mode: 'schedules-otd',
                    windowStart: lastWeek.week_start,
                    windowEnd: lastWeek.week_end,
                    contextLabel: `Latest week (${lastWeek.label})`,
                  });
                }}
                onKeyDown={onTileKey}
                title="View OTD-eligible schedules for the latest week"
              >
                <KPICell
                  label="OTD (latest)"
                  value={<CircularProgress percentage={lastOtd} size={68} strokeWidth={6} color={performanceColor(lastOtd)} />}
                />
              </div>
            )}
            <div
              className={tileBase}
              role="button"
              tabIndex={0}
              onClick={() => setAppealsOpen(true)}
              onKeyDown={onTileKey}
              title="View delivery appeals raised in the last 12 weeks"
            >
              <KPICell label="Appeals (12wk)" value={<span className="text-2xl font-semibold tabular-nums">{totalAppeals.toLocaleString()}</span>} />
            </div>
          </div>
        </SectionFrame>
        <SectionFrame title="Cost" accent="blue" subtitle="last 12 weeks">
          <div className="grid grid-cols-3 gap-3">
            <div
              className={tileBase}
              role="button"
              tabIndex={0}
              onClick={() => openAggregate('schedules-received', 'Last 12 weeks')}
              onKeyDown={onTileKey}
              title="View schedules delivered against in the last 12 weeks"
            >
              <KPICell label="Received (12wk)" value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(totalReceived)}</span>} foot={`of ${formatCurrency(totalCommitted)} due`} />
            </div>
            <div
              className={tileBase}
              role="button"
              tabIndex={0}
              onClick={() => openAggregate('schedules-open', 'Last 12 weeks')}
              onKeyDown={onTileKey}
              title="View schedules with outstanding balance from the last 12 weeks"
            >
              <KPICell label="Open Value" value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(totalOpen)}</span>} foot="still owed (12wk)" />
            </div>
            <div
              className={tileBase}
              role="button"
              tabIndex={0}
              onClick={() => setModal({
                mode: 'schedules-portfolio',
                windowStart: data.today,
                windowEnd: data.today,
                contextLabel: 'All forward (now)',
              })}
              onKeyDown={onTileKey}
              title="View all forward-dated open schedules"
            >
              <KPICell label="Portfolio Open" value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(data.portfolio.open_orders_value)}</span>} foot="all forward (now)" />
            </div>
          </div>
        </SectionFrame>
      </div>

      {(showOTIF || showOTD) && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">
              {showOTIF && showOTD ? 'OTIF & OTD' : showOTIF ? 'OTIF' : 'OTD'} — 12-Week Trend
            </CardTitle>
            <p className="text-[11px] text-muted-foreground">Target {TARGET_PCT}% (dashed) · click a point to drill in</p>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={trend}
                  margin={{ top: 5, right: 10, bottom: 0, left: -10 }}
                  style={{ cursor: 'pointer' }}
                  onClick={(raw: unknown) => {
                    const { weekLabel, segment } = extractChartClick(raw);
                    const w = findWeek(weekLabel);
                    if (!w) return;
                    // OTIF vs OTD by which series the cursor was over.
                    // Default to OTD (broader bucket) when recharts doesn't
                    // surface a dataKey (clicking on whitespace near a tick).
                    // When only one series is rendered, force that mode.
                    let mode: PerWeekDrilldownMode;
                    if (!showOTIF) mode = 'schedules-otd';
                    else if (!showOTD) mode = 'schedules-otif';
                    else mode = segment === 'otif' ? 'schedules-otif' : 'schedules-otd';
                    openWeek(mode, w);
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} unit="%" />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => `${Math.round(v)}%`} />
                  <ReferenceLine y={TARGET_PCT} stroke={PALETTE.target} strokeDasharray="4 4" />
                  {showOTIF && (
                    <Line type="monotone" dataKey="otif" stroke={PALETTE.otif} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="OTIF %" />
                  )}
                  {showOTD && (
                    <Line type="monotone" dataKey="otd"  stroke={PALETTE.otd}  strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="OTD %" />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Weekly Schedule Value — Received vs Open</CardTitle>
            <p className="text-[11px] text-muted-foreground">Click a bar to view that week's lines · received (blue) vs open (grey)</p>
          </CardHeader>
          <CardContent>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={trend}
                  margin={{ top: 5, right: 10, bottom: 0, left: -10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000000 ? `£${(v/1000000).toFixed(1)}M` : v >= 1000 ? `£${(v/1000).toFixed(0)}k` : `£${v}`} />
                  <Tooltip cursor={{ fill: '#e2e8f0', fillOpacity: 0.7 }} contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => formatCurrency(v)} />
                  <Bar dataKey="received" stackId="cost" fill={PALETTE.spend} radius={[0, 0, 0, 0]} name="Received" cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onCostSegmentClick('received')} />
                  <Bar dataKey="open"     stackId="cost" fill="#cbd5e1" radius={[3, 3, 0, 0]} name="Open" cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onCostSegmentClick('open')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Quality — NCRs &amp; {qualityLabel}</CardTitle>
            <p className="text-[11px] text-muted-foreground">Bars: NCRs raised · Line: {qualityLabel} defective · click a bar to drill into NCRs</p>
          </CardHeader>
          <CardContent>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={trend}
                  margin={{ top: 5, right: 10, bottom: 0, left: -10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: '#e2e8f0', fillOpacity: 0.7 }} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Bar yAxisId="left" dataKey="ncrs" fill={PALETTE.ncr} radius={[3, 3, 0, 0]} name="NCRs" cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onNcrBarClick} />
                  <Line yAxisId="right" type="monotone" dataKey="ppm" stroke={PALETTE.ppk} strokeWidth={2.5} dot={{ r: 2 }} name={qualityLabel} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {modal && (
        <PerWeekDrilldownModal
          open={!!modal}
          onClose={() => setModal(null)}
          mode={modal.mode}
          windowStart={modal.windowStart}
          windowEnd={modal.windowEnd}
          todayIso={data.today}
          defaultFirmDays={data.default_firm_days}
          filters={modalFilters}
          contextLabel={modal.contextLabel}
        />
      )}

      <YTDDrilldownModal
        open={appealsOpen}
        onClose={() => setAppealsOpen(false)}
        mode="delivery_appeals"
        filters={modalFilters}
        periodStart={windowStart}
        periodEnd={windowEnd}
        todayIso={data.today}
        periodLabel="Last 12 weeks"
      />
    </div>
  );
}
